import { FieldProps } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileUploadInputStatus,
  FileUploadProperty,
  FileUploadThumbnail,
  FILE_UPLOAD_INPUT_STATUS,
  FormPageInputData,
  FormPageInputItemFileUploadPiclib,
} from 'shared';
import { useFileUpload } from 'store';
import { hasErrorMessage } from '../../shared';
import { useFieldPropsHandler } from '../../useFieldPropsHandler';
import { isFileUploadStateReady } from '../models';
import {
  FILE_UPLOAD_RESULT_TYPE,
  useFileUploadEventHandler,
} from '../useFileUploadEventHandler';
import { useUploadedFileHandler } from '../useUploadedFileHandler';

const sleep = (timeout: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, timeout);
  });

type Payload = {
  property: FileUploadProperty;
  fieldProps: FieldProps<FormPageInputItemFileUploadPiclib, FormPageInputData>;
  submitted: boolean;
};

type ReturnType = {
  onSelect: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  onReload: () => Promise<void>;
  onChange: (file: File) => Promise<void>;
  displayError: boolean;
  sessionError: boolean;
  reloading: boolean;
  errorMessages: string[];
  status: FileUploadInputStatus;
  thumbnail: FileUploadThumbnail | undefined;
};

export const useFileUploadHandler = ({
  property,
  fieldProps,
  submitted,
}: Payload): ReturnType => {
  const [status, setStatus] = useState<FileUploadInputStatus>(
    FILE_UPLOAD_INPUT_STATUS.notReady
  );
  const [itemErrorMessages, setItemErrorMessages] = useState<string[]>([]);
  const fileUpload = useFileUpload();
  const { propertyId } = property;
  const { subjectId } = property.inputFormat;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemFileUploadPiclib>({
    fieldProps,
    propertyId,
  });

  const handleUpdateNextState = useCallback(
    (nextStatus: Extract<FileUploadInputStatus, 'ready' | 'hasFile'>) => {
      setStatus(nextStatus);
    },
    []
  );

  const handleFileNotFound = useCallback(() => {
    setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
    updateFieldValue('fileSrn', '');
  }, [updateFieldValue]);

  const handleNeedsToReload = useCallback(() => {
    setStatus(FILE_UPLOAD_INPUT_STATUS.needsToReload);
    setItemErrorMessages([
      fileUpload.setting?.errorMessages.GET_UPLOADED_FILE_INFO_ERROR ?? '',
    ]);
  }, [fileUpload.setting?.errorMessages.GET_UPLOADED_FILE_INFO_ERROR]);

  const { sessionError, prepare, reload, reloading } = useUploadedFileHandler({
    propertyId,
    subjectId,
    onUpdateNextState: handleUpdateNextState,
    onFileNotFound: handleFileNotFound,
    onNeedsToReload: handleNeedsToReload,
  });

  const errorMessages = useMemo(
    () => [...itemErrorMessages, ...(fieldErrorMessages ?? [])],
    [fieldErrorMessages, itemErrorMessages]
  );

  const { uploadAndVerifyFile, deleteFromTemporaryFile } =
    useFileUploadEventHandler(property);

  const handleSelect = useCallback(
    async (file: File) => {
      const validStatus =
        status === FILE_UPLOAD_INPUT_STATUS.ready ||
        status === FILE_UPLOAD_INPUT_STATUS.hasFile;
      const beforeStatus = status;
      if (!isFileUploadStateReady(fileUpload) || !validStatus) {
        return;
      }
      setItemErrorMessages([]);
      setStatus(FILE_UPLOAD_INPUT_STATUS.uploading);
      const { session, setting } = fileUpload;

      const result = await uploadAndVerifyFile({
        file,
        session,
        setting,
      });
      if (result.type === FILE_UPLOAD_RESULT_TYPE.success) {
        updateFieldValue('fileSrn', result.fileSrn);
        setStatus(FILE_UPLOAD_INPUT_STATUS.uploadComplete);
        await sleep(1000);
        setStatus(FILE_UPLOAD_INPUT_STATUS.hasFile);
      } else {
        setStatus(beforeStatus);
        setItemErrorMessages(result.errorMessages);
      }
      updateFieldTouched('fileSrn');
    },
    [
      fileUpload,
      status,
      uploadAndVerifyFile,
      updateFieldTouched,
      updateFieldValue,
    ]
  );

  const handleDelete = useCallback(async () => {
    if (
      !isFileUploadStateReady(fileUpload) ||
      status !== FILE_UPLOAD_INPUT_STATUS.hasFile
    ) {
      return;
    }
    setItemErrorMessages([]);
    setStatus(FILE_UPLOAD_INPUT_STATUS.deleting);
    const { session, setting } = fileUpload;

    const result = await deleteFromTemporaryFile({
      session,
      setting,
    });
    if (result.type === FILE_UPLOAD_RESULT_TYPE.success) {
      updateFieldValue('fileSrn', result.fileSrn);
      setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
    } else {
      setStatus(FILE_UPLOAD_INPUT_STATUS.hasFile);
      setItemErrorMessages(result.errorMessages);
    }
    updateFieldTouched('fileSrn');
  }, [
    fileUpload,
    status,
    deleteFromTemporaryFile,
    updateFieldTouched,
    updateFieldValue,
  ]);

  const handleReload = useCallback(async () => {
    const fileSrn = fieldProps.field.value.fileSrn;
    if (!fileSrn) {
      setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
      return;
    }
    await reload({ fileSrn });
  }, [fieldProps.field.value.fileSrn, reload]);

  const initialize = useCallback(async () => {
    if (status !== FILE_UPLOAD_INPUT_STATUS.notReady) {
      return;
    }
    const fileSrn = fieldProps.field.value.fileSrn;
    await prepare({
      fileSrn,
    });
  }, [fieldProps.field.value.fileSrn, prepare, status]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const hasError =
    (fieldTouched || submitted) && hasErrorMessage(errorMessages);
  const displayError =
    status === FILE_UPLOAD_INPUT_STATUS.needsToReload || hasError;

  return {
    onSelect: handleSelect,
    onDelete: handleDelete,
    onChange: handleSelect,
    onReload: handleReload,
    displayError,
    sessionError,
    reloading,
    errorMessages,
    status,
    thumbnail: fileUpload.items[subjectId]?.thumbnail,
  };
};
